import { useEffect, useState } from "react";
import { Mail, Send, ShieldCheck, ShieldAlert, Sparkles, BellRing } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const inputCls = "w-full px-3 py-2 rounded-xl border border-stone-300 focus:border-amber-600 outline-none text-sm";

/** Paste a list of emails, tweak the draft, send the beta invitation batch. */
export function BetaInvitesCard() {
  const [draft, setDraft] = useState(null);
  const [emails, setEmails] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const load = () => api.get("/admin/beta-invites/draft").then((r) => { setDraft(r.data); setSubject((s) => s || r.data.subject); setBody((b) => b || r.data.body); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!draft) return null;
  const count = emails.split(/[\s,;]+/).filter(Boolean).length;
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/beta-invites", { emails, subject, body });
      setResult(data);
      toast.success(`${data.sent} invite${data.sent === 1 ? "" : "s"} sent${data.failed ? ` · ${data.failed} failed` : ""}`);
      setEmails("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <section className="warm-card p-6" data-testid="beta-invites-card">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-amber-800"><Mail className="w-3.5 h-3.5" /> Invite beta testers</div>
      <h2 className="font-display text-lg font-bold text-stone-900 mt-1">One batch, up to {draft.max_batch} people</h2>
      <p className="text-xs text-stone-600 mt-1">Each person gets the same email with your beta link <span className="font-mono text-[11px]">{draft.link}</span>. Replies go to {draft.reply_to}. Sending from your verified domain.</p>
      <div className="grid lg:grid-cols-2 gap-4 mt-4">
        <div className="space-y-3">
          <label className="block text-xs font-bold uppercase tracking-widest text-stone-500">Emails <span className="normal-case font-normal">— one per line, or comma-separated</span>
            <textarea value={emails} onChange={(e) => setEmails(e.target.value)} rows={8} className={`${inputCls} mt-1 font-mono text-xs`} placeholder={"alex@example.com\nsam@example.com"} data-testid="invite-emails" /></label>
          <button onClick={send} disabled={busy || !count} className="amber-btn text-sm w-full disabled:opacity-50" data-testid="invite-send">
            <Send className="w-4 h-4 mr-2" /> {busy ? "Sending…" : `Send ${count || ""} invite${count === 1 ? "" : "s"}`}
          </button>
          {result && (
            <ul className="text-xs space-y-1 max-h-32 overflow-auto" data-testid="invite-results">
              {result.results.map((r) => <li key={r.email} className={r.ok ? "text-emerald-800" : "text-red-700"}>{r.ok ? "Sent" : `Failed (${r.error})`} · {r.email}</li>)}
              {result.invalid.map((e) => <li key={e} className="text-stone-500">Skipped (not an email) · {e}</li>)}
            </ul>
          )}
        </div>
        <div className="space-y-3">
          <label className="block text-xs font-bold uppercase tracking-widest text-stone-500">Subject<input value={subject} onChange={(e) => setSubject(e.target.value)} className={`${inputCls} mt-1`} data-testid="invite-subject" /></label>
          <label className="block text-xs font-bold uppercase tracking-widest text-stone-500">Message<textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} className={`${inputCls} mt-1`} data-testid="invite-body" /></label>
        </div>
      </div>
      {draft.progress.length > 0 && <InviteProgress rows={draft.progress} remindDue={draft.remind_due} remindDays={draft.remind_after_days} onReminded={load} />}
    </section>
  );
}

const STAGES = ["failed", "sent", "signed_up", "checked_in"];
const STAGE_LABEL = { failed: "Not delivered", sent: "Invited", signed_up: "Signed up", checked_in: "First check-in" };
const STAGE_CLS = { failed: "bg-red-50 text-red-700", sent: "bg-stone-100 text-stone-600", signed_up: "bg-amber-100 text-amber-800", checked_in: "bg-emerald-100 text-emerald-800" };
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—");

/** Invited → Signed up → First check-in, per email. */
function InviteProgress({ rows, remindDue, remindDays, onReminded }) {
  const [busy, setBusy] = useState(false);
  const remind = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/beta-invites/remind");
      toast.success(`${data.reminded} reminder${data.reminded === 1 ? "" : "s"} sent${data.failed ? ` · ${data.failed} failed` : ""}`);
      onReminded?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const counts = STAGES.reduce((a, s) => ({ ...a, [s]: rows.filter((r) => r.stage === s).length }), {});
  return (
    <div className="mt-5" data-testid="invite-progress">
      <div className="flex items-center gap-2 flex-wrap text-[11px] font-semibold">
        <span className="uppercase tracking-widest text-stone-500 mr-1">Progress</span>
        {STAGES.filter((s) => counts[s]).map((s) => <span key={s} className={`px-2 py-0.5 rounded-full ${STAGE_CLS[s]}`} data-testid={`invite-stage-count-${s}`}>{counts[s]} {STAGE_LABEL[s]}</span>)}
        <span className="flex-1" />
        <button onClick={remind} disabled={busy || !remindDue} data-testid="invite-remind"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white transition-colors disabled:opacity-50"
          title={remindDue ? `Gentle one-time follow-up to people invited ${remindDays}+ days ago who haven't signed up` : `Nobody is ${remindDays}+ days past their invite without signing up`}>
          <BellRing className="w-3.5 h-3.5" /> {busy ? "Sending…" : remindDue ? `Remind ${remindDue} tester${remindDue === 1 ? "" : "s"}` : "No reminders due"}
        </button>
      </div>
      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-left text-[10px] uppercase tracking-widest text-stone-500"><th className="py-1.5 pr-3">Email</th><th className="py-1.5 pr-3">Invited</th><th className="py-1.5 pr-3">Signed up</th><th className="py-1.5 pr-3">First check-in</th><th className="py-1.5">Stage</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.email} className="border-t border-stone-100" data-testid={`invite-row-${r.email}`}>
                <td className="py-1.5 pr-3 font-mono text-[11px] text-stone-800">{r.email}{r.roles.length > 0 && <span className="ml-1 text-stone-400">({r.roles.join(" + ")})</span>}</td>
                <td className="py-1.5 pr-3 text-stone-600">{day(r.invited_at)}</td>
                <td className="py-1.5 pr-3 text-stone-600">{day(r.signed_up_at)}</td>
                <td className="py-1.5 pr-3 text-stone-600">{day(r.first_checkin_at)}</td>
                <td className="py-1.5"><span className={`px-2 py-0.5 rounded-full font-semibold ${STAGE_CLS[r.stage]}`}>{STAGE_LABEL[r.stage]}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Is the HIPAA (linked-clinician) AI lane on BastionGPT yet? One tap tests the key. */
export function BastionReadinessCard() {
  const [lanes, setLanes] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/ai-lanes").then((r) => setLanes(r.data)).catch(() => {}); }, []);
  if (!lanes) return null;
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/ai-lanes/test"); toast.success(data.ok ? "BastionGPT answered — the key works." : "BastionGPT replied, but unexpectedly."); if (data.note) toast(data.note, { duration: 9000 }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const Icon = lanes.ready ? ShieldCheck : ShieldAlert;
  return (
    <section className={`warm-card p-6 ${lanes.ready ? "" : "border-amber-200"}`} data-testid="bastion-card">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-amber-800"><Sparkles className="w-3.5 h-3.5" /> AI lanes</div>
      <div className="flex items-start gap-3 mt-2 flex-wrap">
        <Icon className={`w-6 h-6 shrink-0 ${lanes.ready ? "text-emerald-700" : "text-amber-700"}`} />
        <div className="flex-1 min-w-[14rem]">
          <h2 className="font-display text-lg font-bold text-stone-900" data-testid="bastion-status">{lanes.ready ? "Linked people you support are on BastionGPT" : "BastionGPT not active yet"}</h2>
          <ul className="text-xs text-stone-600 mt-1 space-y-0.5">
            <li>General lane (solo people you support): <strong>{lanes.general.provider}</strong> · {lanes.general.model}</li>
            <li>Coach-linked lane: <strong>{lanes.phi.provider || "unavailable"}</strong>{lanes.phi.beta_fallback && <span className="text-amber-800"> · beta fallback to the general lane</span>} · {lanes.linked_clients} linked member{lanes.linked_clients === 1 ? "" : "s"}</li>
            <li>Key in environment: <strong>{lanes.bastion_key_present ? "yes" : "no"}</strong> · de-identification: {lanes.deid}</li>
          </ul>
          {!lanes.ready && <p className="text-xs text-stone-500 mt-2">When the BAA key arrives: add <span className="font-mono">BASTION_API_KEY</span>, set <span className="font-mono">AI_PROVIDER_PHI=bastion</span> and <span className="font-mono">AI_ALLOW_GENERAL_FOR_LINKED=false</span>, restart, then test here.</p>}
        </div>
        <button onClick={test} disabled={busy} className="text-xs font-semibold px-3 py-2 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white transition-colors disabled:opacity-50" data-testid="bastion-test">{busy ? "Testing…" : "Test the key"}</button>
      </div>
    </section>
  );
}

export function HipaaDocsCard() {
  const [docs, setDocs] = useState([]);
  useEffect(() => { api.get("/admin/docs").then((r) => setDocs(r.data.docs)).catch(() => {}); }, []);
  if (!docs.length) return null;
  return (
    <section className="warm-card p-6" data-testid="hipaa-docs-card">
      <div className="text-[10px] font-bold uppercase tracking-widest text-rose-800">Documents</div>
      <p className="text-sm text-stone-600 mt-1">Working documents for RCT NeuroAcademy. HIPAA and Bastion papers are kept in the Archive. Downloads are audit-logged.</p>
      <ul className="mt-3 divide-y divide-stone-100">
        {[...docs.filter((d) => d.group !== "archive"), { key: "__archive", title: "Archive — HIPAA & Bastion (clinical-era papers)", header: true },
          ...docs.filter((d) => d.group === "archive")].map((d) => d.header ? <li key={d.key} className="pt-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-stone-400" data-testid="docs-archive-header">{d.title}</li> : (
          <li key={d.key} className="py-2.5 flex items-center justify-between gap-3 flex-wrap">
            <div><div className="text-sm font-semibold text-stone-800">{d.title}</div><div className="text-xs text-stone-500">{d.blurb}</div></div>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/docs/${d.key}`} target="_blank" rel="noreferrer" data-testid={`hipaa-doc-${d.key}`}
              className={`text-xs font-semibold px-4 py-2 rounded-full ${d.available ? "bg-stone-900 text-white hover:bg-rose-800" : "bg-stone-100 text-stone-400 pointer-events-none"}`}>{d.available ? "Download" : "Not built"}</a>
          </li>
        ))}
      </ul>
    </section>
  );
}
